import React, { useState } from 'react';
import { Button, UnorderedList, UnorderedListItem } from '@keplerco/core';
import styles from './json-viewer.module.css';
import classNames from 'classnames';
import { IJsonViewerProps } from './json-viewer.models';

export function JsonViewer({ data, isPreview = false, hideRawToggle = false, recordCount, enableTextHighlighting = false }: IJsonViewerProps): JSX.Element {
  const [showRawJson, setShowRawJson] = useState<boolean>(false);

  // Format the response for display
  const formatJson = (jsonData: any) => {
    if (!jsonData) return null;

    // For arrays at the top level, render each item in its own card
    if (Array.isArray(jsonData)) {
      return (
        <div className={styles.section}>
          {jsonData.length === 0 ? (
            <div>Empty array</div>
          ) : (
            jsonData.map((item, index) => (
              <div key={index} className={classNames('card', styles.card)}>
                <div className={styles.cardContent}>{renderContent(item, isPreview)}</div>
              </div>
            ))
          )}
        </div>
      );
    }

    // For single objects at the top level, wrap in a single card
    return (
      <div className={classNames('card', styles.card)}>
        <div className={styles.cardContent}>{renderContent(jsonData, isPreview)}</div>
      </div>
    );
  };

  // Render the content of a JSON object or value
  const renderContent = (jsonData: any, isPreview = false) => {
    if (typeof jsonData === 'string') {
      return <div>{jsonData}</div>;
    }

    if (Array.isArray(jsonData)) {
      if (jsonData.length === 0) {
        return <div>Empty array</div>;
      }

      // Check if array contains primitive values
      if (jsonData.every(item => typeof item !== 'object' || item === null)) {
        return (
          <UnorderedList>
            {jsonData.map((item, index) => (
              <UnorderedListItem key={index}>{item === null ? 'null' : String(item)}</UnorderedListItem>
            ))}
          </UnorderedList>
        );
      }

      // For nested arrays of objects, render each item without additional cards
      return (
        <div className={styles.nestedSection}>
          {jsonData.map((item, index) => (
            <div key={index} className={styles.nestedSectionItem}>
              {renderContent(item, isPreview)}
            </div>
          ))}
        </div>
      );
    }

    if (typeof jsonData === 'object' && jsonData !== null) {
      return renderObjectProperties(jsonData, isPreview);
    }

    // Handle primitive values
    return <div>{String(jsonData)}</div>;
  };

  // Render object properties as a list of label/value pairs
  const renderObjectProperties = (obj: Record<string, any>, isPreview = false) => {
    if (Object.keys(obj).length === 0) {
      return <div>Empty object</div>;
    }

    const keys = Object.keys(obj);

    return (
      <div className={styles.propertyList}>
        {keys.map(key => (
          <div key={key} className={styles.property}>
            <h5 className={styles.propertyKey}>{formatPropertyName(key)}</h5>
            <div style={{ marginLeft: 8 }}>{renderPropertyValue(obj[key], isPreview)}</div>
          </div>
        ))}
      </div>
    );
  };

  // Format a property name to be more readable
  const formatPropertyName = (name: string) => {
    // Convert camelCase or snake_case to Title Case with spaces
    return name
      .replace(/([A-Z])/g, ' $1') // Insert a space before all capital letters
      .replace(/_/g, ' ') // Replace underscores with spaces
      .replace(/^\w/, c => c.toUpperCase()); // Capitalize the first letter
  };

  // Render a property value based on its type
  const renderPropertyValue = (value: any, isPreview = false): JSX.Element => {
    if (value === null) {
      return <span>null</span>;
    }

    if (typeof value === 'boolean') {
      return <span>{value.toString()}</span>;
    }

    if (typeof value === 'number') {
      return <span>{value}</span>;
    }

    if (typeof value === 'string') {
      // Check if the string is a URL
      if (value.match(/^https?:\/\//i)) {
        return (
          <a href={value} target="_blank" rel="noopener noreferrer">
            {value}
          </a>
        );
      }

      // If text highlighting is enabled and the string contains <b> tags
      if (enableTextHighlighting && value.includes('<b>')) {
        return <span dangerouslySetInnerHTML={{ __html: value }} />;
      }

      return <span>{value}</span>;
    }

    if (Array.isArray(value)) {
      if (value.length === 0) {
        return <span>Empty array</span>;
      }

      // Check if array contains primitive values
      if (value.every(item => typeof item !== 'object' || item === null)) {
        return (
          <UnorderedList>
            {value.map((item, index) => (
              <UnorderedListItem key={index}>{item === null ? 'null' : String(item)}</UnorderedListItem>
            ))}
          </UnorderedList>
        );
      }

      // For nested arrays, render the content directly
      return renderContent(value, isPreview);
    }

    // For objects, recursively render
    if (typeof value === 'object' && value !== null) {
      return renderObjectProperties(value, isPreview);
    }

    return <span>{String(value)}</span>;
  };

  // Generate the preview label text
  const getPreviewLabel = () => {
    if (recordCount) {
      return `Preview 1 of ${recordCount}`;
    }
    return 'Preview';
  };

  // Determine if we should show ellipsis based on preview mode
  const shouldShowEllipsis = isPreview && (recordCount === undefined || recordCount > 1);

  return (
    <div className={styles.viewer}>
      {isPreview && <div className={styles.previewLabel}>{getPreviewLabel()}</div>}

      {!hideRawToggle && (
        <div className={styles.toggle}>
          <Button type="button" onClick={() => setShowRawJson(currentState => !currentState)}>
            {showRawJson ? 'Show Formatted View' : 'Show Raw JSON'}
          </Button>
        </div>
      )}

      {!!data && <React.Fragment>{showRawJson ? <pre className={styles.raw}>{JSON.stringify(data, null, 2)}</pre> : formatJson(data)}</React.Fragment>}

      {shouldShowEllipsis && <div className={styles.ellipsis}>...</div>}
    </div>
  );
}
